import { useRef, useState, useEffect } from "react";
import { motion } from "framer-motion";
import { MoveHorizontal } from "lucide-react";
import { resolveImage } from "@/api";

const CompareSlider = ({ item }) => {
  const ref = useRef(null);
  const [pos, setPos] = useState(50);
  const dragging = useRef(false);

  useEffect(() => {
    const move = (e) => {
      if (!dragging.current || !ref.current) return;
      const rect = ref.current.getBoundingClientRect();
      const x = Math.min(Math.max(e.clientX - rect.left, 0), rect.width);
      setPos((x / rect.width) * 100);
    };
    const up = () => { dragging.current = false; };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
    return () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
  }, []);

  return (
    <div ref={ref} data-testid={`compare-slider-${item.id}`}
         className="relative w-full aspect-[4/3] overflow-hidden select-none cursor-ew-resize touch-none border border-amber-500/20"
         onPointerDown={(e) => { dragging.current = true; e.currentTarget.setPointerCapture?.(e.pointerId); }}>
      <img src={resolveImage(item.after)} alt={`${item.title} after`} draggable={false}
           className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 overflow-hidden" style={{ width: `${pos}%` }}>
        <img src={resolveImage(item.before)} alt={`${item.title} before`} draggable={false}
             className="absolute top-0 left-0 h-full max-w-none object-cover" style={{ width: ref.current?.offsetWidth || "100%" }} />
      </div>
      <div className="absolute top-0 bottom-0 w-0.5 bg-amber-500" style={{ left: `${pos}%` }}>
        <div className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-9 h-9 bg-amber-500 flex items-center justify-center shadow-lg">
          <MoveHorizontal className="w-5 h-5 text-slate-950" />
        </div>
      </div>
      <span className="absolute top-3 left-3 text-[10px] font-mono-brand uppercase tracking-[0.2em] bg-slate-950/80 text-slate-300 px-2 py-1">Before</span>
      <span className="absolute top-3 right-3 text-[10px] font-mono-brand uppercase tracking-[0.2em] bg-amber-500 text-slate-950 px-2 py-1">After</span>
    </div>
  );
};

export const BeforeAfterGallery = ({ items }) => {
  const [filter, setFilter] = useState("All");
  const categories = ["All", ...new Set(items.map((i) => i.category))];
  const visible = filter === "All" ? items : items.filter((i) => i.category === filter);

  return (
    <section id="gallery" data-testid="gallery-section" className="py-20 lg:py-32 bg-[#111827] border-y border-amber-500/15">
      <div className="px-4 sm:px-8 lg:px-12 max-w-7xl mx-auto">
        <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.7 }}
                    className="mb-12 flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="text-xs font-mono-brand uppercase tracking-[0.25em] text-amber-400 mb-4">02 — The Proof</p>
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-condensed font-extrabold uppercase tracking-tight leading-none">
              Before <span className="text-amber-500">/</span> After
            </h2>
          </div>
          <div className="flex flex-wrap gap-2" data-testid="gallery-filters">
            {categories.map((c) => (
              <button key={c} onClick={() => setFilter(c)} data-testid={`gallery-filter-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      className={`text-xs font-mono-brand uppercase tracking-[0.15em] px-4 py-2 border transition-all duration-300 ${filter === c ? "bg-amber-500 text-slate-950 border-amber-500" : "text-slate-400 border-slate-700 hover:border-amber-500/50 hover:text-amber-400"}`}>
                {c}
              </button>
            ))}
          </div>
        </motion.div>
        {visible.length === 0 ? (
          <p className="text-slate-500 text-sm" data-testid="gallery-empty">No projects yet — check back soon.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {visible.map((item, i) => (
              <motion.figure key={item.id} data-testid={`gallery-item-${item.id}`}
                             initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }}
                             viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.55, delay: (i % 3) * 0.1 }}>
                <CompareSlider item={item} />
                <figcaption className="flex items-center justify-between mt-3">
                  <span className="font-condensed font-bold uppercase tracking-wide text-slate-200">{item.title}</span>
                  <span className="text-xs font-mono-brand uppercase tracking-[0.15em] text-amber-400">{item.category}</span>
                </figcaption>
              </motion.figure>
            ))}
          </div>
        )}
        <p className="text-xs font-mono-brand uppercase tracking-[0.2em] text-slate-600 mt-8 text-center" data-testid="gallery-hint">
          Drag the handle to compare
        </p>
      </div>
    </section>
  );
};
